
import Vue from 'vue';
import {
  Clause, StructuralClause, ConditionalClause, Label, FileDocument, Realm, User, Publisher,
} from '@/model';
import LabelPicker from '@/components/LabelPicker.vue';
import DocumentPicker from '@/components/DocumentPicker.vue';
import { notEmpty } from '@/util/array-util';
import deepEqual from '@/util/deep-equals';
import { asSimpleClause } from '@/controllers/FragmentSelectionController';
import UserPicker from '@/components/UserPicker.vue';
import users from '@/store/users';
import PublisherPicker from '@/components/PublisherPicker.vue';
import publishers from '@/store/publisher';
import { documentStatusOptions } from '@/store/ui-config';

interface SimpleClauseValues {
  documents: FileDocument[];
  labels: Label[];
  labelsExclude: Label[];
  anyLabel: boolean;
}

export default Vue.extend({
  name: 'SelectionClauseEditor',
  components: {
    LabelPicker, DocumentPicker, UserPicker, PublisherPicker,
  },

  props: {
    value: { type: Object as () => Clause | undefined },
    depth: { type: Number, default: 0 },
    simplifiable: { type: Boolean, default: true },
    documents: { type: Array as () => FileDocument[] },
    labels: { type: Array as () => Label[] },
    realms: { type: Array as () => Realm[] },
    advancedMode: { type: Boolean, default: false },
  },

  data() {
    return {
      edit: { ...this.value } as ConditionalClause | StructuralClause,
      showExclude: false,
    };
  },

  watch: {
    edit: {
      deep: true,
      handler() {
        this.$emit('input', this.edit);
      },
    },
    value: {
      immediate: true,
      handler() {
        if (this.value && !deepEqual(this.value, this.edit)) {
          this.edit = { ...this.value };
        }
      },
    },
  },

  computed: {
    effectiveAdvanced(): boolean {
      return !this.simplifiable || this.advancedMode;
    },
    simpleLabels: {
      get(): Label[] {
        return this.simpleValues.labels;
      },
      set(labels: Label[]) {
        this.setSimpleClause({ ...this.simpleValues, labels });
      },
    },
    simpleLabelsExclude: {
      get(): Label[] {
        return this.simpleValues.labelsExclude;
      },
      set(labelsExclude: Label[]) {
        this.setSimpleClause({ ...this.simpleValues, labelsExclude });
      },
    },
    simpleDocuments: {
      get(): FileDocument[] {
        return this.simpleValues.documents;
      },
      set(documents: FileDocument[]) {
        this.setSimpleClause({ ...this.simpleValues, documents });
      },
    },
    anyLabel: {
      get(): boolean {
        return this.simpleValues.anyLabel;
      },
      set(anyLabel: boolean) {
        this.setSimpleClause({ ...this.simpleValues, anyLabel });
      },
    },
    anyLabelToggle: {
      get(): string {
        return this.simpleValues.anyLabel ? 'any' : 'all';
      },
      set(val: string) {
        this.setSimpleClause({ ...this.simpleValues, anyLabel: val === 'any' });
      },
    },
    simpleValues(): SimpleClauseValues {
      if (this.labels && this.documents && this.realms) {
        const simple = this.asSimpleValues(this.edit);
        if (simple) {
          return simple;
        }
      }
      return {
        documents: [],
        labels: [],
        labelsExclude: [],
        anyLabel: false,
      };
    },
  },

  methods: {
    addClause() {
      if (this.edit.type === 'and' || this.edit.type === 'or') {
        this.edit = { ...this.edit, clauses: [...this.edit.clauses, { type: 'in', field: 'label', values: [] } as Clause] };
      }
    },
    editClause(clause: Clause, index: number) {
      if (this.edit.type === 'and' || this.edit.type === 'or') {
        this.edit = { ...this.edit, clauses: this.edit.clauses.map((v, i) => (i === index ? clause : v)) };
      }
    },
    setSimpleClause(simple: SimpleClauseValues) {
      this.edit = asSimpleClause(
        simple.documents.map((d) => d.id),
        simple.labels.map((l) => l.id),
        simple.labelsExclude.map((l) => l.id),
        simple.anyLabel,
      );
    },
    remove() {
      this.$emit('remove');
    },
    removeClause(index: number) {
      if (this.edit.type === 'and' || this.edit.type === 'or') {
        this.edit = { ...this.edit, clauses: this.edit.clauses.filter((_, i) => i !== index) };
      }
    },
    updateValues(vs: Array<FileDocument | Label | Realm | User | Publisher>) {
      if (this.edit.type === 'in') {
        this.edit = { ...this.edit, values: vs.map((a) => a.id) };
      }
    },
    changeType(type: Clause['type']) {
      if (type !== this.edit.type) {
        this.edit = type === 'in'
          ? { type, field: 'label', values: [] }
          : {
            type,
            clauses: this.edit.type === 'and' || this.edit.type === 'or' ? this.edit.clauses : [],
          };
      }
    },
    changeIn(field: ConditionalClause['field']) {
      if (this.edit.type === 'in' && field !== this.edit.field) {
        this.edit = { type: 'in', field, values: [] };
      }
    },
    asSimpleValues(clause: Clause): SimpleClauseValues | undefined {
      let simplifiable = false;
      let documents: number[] = [];
      let labels: number[] = [];
      let labelsExclude: number[] = [];
      let anyLabel = false;
      if (clause.type === 'and') {
        simplifiable = true;
        clause.clauses.forEach((sub) => {
          if (sub.type === 'in') {
            if (sub.field === 'document') {
              if (sub.negate) {
                simplifiable = false;
              }
              documents = [...documents, ...sub.values];
            } else if (sub.field === 'label') {
              if (sub.negate) {
                labelsExclude = [...labelsExclude, ...sub.values];
              } else {
                if (labels.length === 0) {
                  if (sub.values.length > 1) {
                    anyLabel = true;
                  }
                } else if (sub.values.length > 1 || anyLabel) {
                  simplifiable = false;
                }
                labels = [...labels, ...sub.values];
              }
            }
          } else {
            simplifiable = false;
          }
        });
      }
      if (simplifiable) {
        return {
          documents: documents.map(this.document).filter(notEmpty),
          labels: labels.map(this.label).filter(notEmpty),
          labelsExclude: labelsExclude.map(this.label).filter(notEmpty),
          anyLabel,
        };
      }
      return undefined;
    },
    document(id: number): FileDocument | undefined {
      return this.documents.find((d) => d.id === id);
    },
    label(id: number): Label | undefined {
      return this.labels.find((l) => l.id === id);
    },
    realm(id: number) {
      return this.realms.find((r) => r.id === id);
    },
    user(id: number) {
      return users.getters.lookup.get(id);
    },
    publisher(id: number) {
      return publishers.getters.lookup.get(id);
    },
    statusOptions(clause: StructuralClause | ConditionalClause) {
      return documentStatusOptions(clause.type === 'in' && clause.field === 'status' ? clause.values : []);
    },
  },

});
